import Renderer from '../rendering/Renderer.js';
import CameraController from '../rendering/CameraController.js';
import PhysicsEngine from '../engine/PhysicsEngine.js';
import ParticlePool from '../pool/ParticlePool.js';
import ParticleEngine from '../engine/ParticleEngine.js';
import FireworkFactory from '../factory/FireworkFactory.js';
import LaunchController from './LaunchController.js';
import PerformanceMonitor from '../performance/PerformanceMonitor.js';
import UIController from '../ui/UIController.js';

/**
 * FireworksApp类 - 应用程序的主入口和协调中心
 * 
 * 职责:
 * - 初始化所有子系统
 * - 管理主渲染循环
 * - 处理窗口resize事件
 * - 协调各模块间的通信
 * 
 * 验证需求: Requirements 9.2, 9.5
 */
class FireworksApp {
  constructor(containerElement) {
    if (!containerElement) {
      throw new Error('Container element is required');
    }

    this.container = containerElement;
    
    // 核心组件
    this.renderer = null;
    this.cameraController = null;
    this.physicsEngine = null;
    this.particlePool = null;
    this.particleEngine = null;
    this.fireworkFactory = null;
    this.launchController = null;
    this.performanceMonitor = null;
    this.uiController = null;
    
    // 渲染循环
    this.isRunning = false;
    this.animationFrameId = null;
    this.lastTime = performance.now();
    
    // Resize处理
    this.resizeTimeout = null;
    this.resizeDebounceDelay = 150; // 150ms
  }

  /**
   * 初始化应用
   */
  init() {
    try {
      console.log('Initializing 3D Fireworks Display...');
      
      // 1. 初始化渲染器
      this.renderer = new Renderer(this.container);
      this.renderer.init();
      console.log('✓ Renderer initialized');
      
      // 2. 初始化相机控制器
      this.cameraController = new CameraController(
        this.renderer.getCamera(),
        this.renderer.renderer.domElement
      );
      this.cameraController.enableControls();
      console.log('✓ Camera controller initialized');
      
      // 3. 初始化物理引擎
      this.physicsEngine = new PhysicsEngine();
      console.log('✓ Physics engine initialized');
      
      // 4. 初始化粒子池
      this.particlePool = new ParticlePool(5000);
      console.log('✓ Particle pool initialized');
      
      // 5. 初始化粒子引擎
      this.particleEngine = new ParticleEngine(
        this.renderer.getScene(),
        this.particlePool,
        this.physicsEngine
      );
      console.log('✓ Particle engine initialized');
      
      // 6. 初始化烟花工厂
      this.fireworkFactory = new FireworkFactory(
        this.renderer.getScene(),
        this.particleEngine
      );
      console.log('✓ Firework factory initialized');
      
      // 7. 初始化发射控制器
      this.launchController = new LaunchController(this.fireworkFactory);
      console.log('✓ Launch controller initialized');
      
      // 8. 初始化性能监控器
      this.performanceMonitor = new PerformanceMonitor();
      console.log('✓ Performance monitor initialized');
      
      // 9. 初始化UI控制器
      this.uiController = new UIController(this);
      this.uiController.init();
      console.log('✓ UI controller initialized');
      
      // 10. 绑定窗口事件
      this._bindWindowEvents();
      
      console.log('✓ All systems initialized successfully');
      
    } catch (error) {
      console.error('Initialization failed:', error);
      this._handleInitError(error);
      throw error;
    }
  }

  /**
   * 启动应用
   */
  start() {
    if (this.isRunning) {
      console.warn('App is already running');
      return;
    }
    
    console.log('Starting application...');
    this.isRunning = true;
    this.lastTime = performance.now();
    this._animate();
  }

  /**
   * 停止应用
   */
  stop() {
    if (!this.isRunning) {
      console.warn('App is not running');
      return;
    }
    
    console.log('Stopping application...');
    this.isRunning = false;
    
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * 主渲染循环
   * @private
   */
  _animate() {
    if (!this.isRunning) return;
    
    // 请求下一帧
    this.animationFrameId = requestAnimationFrame(() => this._animate());
    
    // 计算时间增量
    const currentTime = performance.now();
    const deltaTime = Math.min((currentTime - this.lastTime) / 1000, 0.1); // 限制最大deltaTime
    this.lastTime = currentTime;
    
    // 更新所有系统
    this.update(deltaTime);
    
    // 渲染场景
    this.renderer.render();
  }

  /**
   * 更新所有系统
   * @param {number} deltaTime - 时间增量（秒）
   */
  update(deltaTime) {
    // 1. 更新性能监控器
    this.performanceMonitor.update(deltaTime);
    
    // 2. 检查是否需要调整质量
    this._checkPerformance();
    
    // 3. 更新相机控制器
    this.cameraController.update(deltaTime);
    
    // 4. 更新发射控制器
    this.launchController.update(deltaTime);
    
    // 5. 更新粒子引擎
    this.particleEngine.update(deltaTime);
    
    // 6. 更新UI显示
    this._updateUI();
  }

  /**
   * 检查性能并调整质量
   * @private
   */
  _checkPerformance() {
    if (this.performanceMonitor.shouldReduceQuality()) {
      this.performanceMonitor.reduceQuality();
      const config = this.performanceMonitor.getQualityConfig();
      this.particleEngine.setQualityLevel(this.performanceMonitor.getQualityLevel());
      console.log('Quality reduced:', config);
    } else if (this.performanceMonitor.shouldIncreaseQuality()) {
      this.performanceMonitor.increaseQuality();
      const config = this.performanceMonitor.getQualityConfig();
      this.particleEngine.setQualityLevel(this.performanceMonitor.getQualityLevel());
      console.log('Quality increased:', config);
    }
    
    // 检查严重性能问题
    if (this.performanceMonitor.isCriticalPerformance()) {
      console.warn('Critical performance detected, clearing particles');
      this.particleEngine.clear();
    }
  }

  /**
   * 更新UI显示
   * @private
   */
  _updateUI() {
    // 更新FPS
    this.uiController.updateFPS(this.performanceMonitor.getFPS());
    
    // 更新粒子数量
    this.uiController.updateParticleCount(this.particleEngine.getActiveParticleCount());
  }

  /**
   * 发射单个烟花（由UI调用）
   */
  launchSingle() {
    this.launchController.launchSingle();
  }

  /**
   * 发射连发烟花（由UI调用）
   * @param {number} count - 烟花数量
   */
  launchBurst(count) {
    this.launchController.launchBurst(count);
  }

  /**
   * 处理窗口大小变化
   */
  resize() {
    if (!this.renderer) return;
    
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    
    this.renderer.setSize(width, height);
    console.log(`Resized to ${width}x${height}`);
  }

  /**
   * 绑定窗口事件
   * @private
   */
  _bindWindowEvents() {
    // Resize事件（带防抖）
    window.addEventListener('resize', () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      this.resizeTimeout = setTimeout(() => {
        this.resize();
      }, this.resizeDebounceDelay);
    });
    
    // 页面可见性变化
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // 页面隐藏时暂停
        if (this.isRunning) {
          this.stop();
        }
      } else {
        // 页面显示时恢复
        if (!this.isRunning) {
          this.start();
        }
      }
    });
  }

  /**
   * 处理初始化错误
   * @param {Error} error - 错误对象
   * @private
   */
  _handleInitError(error) {
    let message = '应用初始化失败';
    
    if (error.message.includes('WebGL')) {
      message = '您的浏览器不支持WebGL，无法运行3D烟花效果';
    } else if (error.message.includes('Container')) {
      message = '找不到容器元素，请刷新页面重试';
    }
    
    // 显示错误消息
    if (this.uiController) {
      this.uiController.showError(message);
    } else {
      // UI控制器未初始化，直接操作DOM
      const errorElement = document.getElementById('error-message');
      const errorText = document.getElementById('error-text');
      if (errorElement && errorText) {
        errorText.textContent = message;
        errorElement.classList.remove('hidden');
      }
    }
  }

  /**
   * 清理资源
   */
  dispose() {
    console.log('Disposing application...');
    
    // 停止渲染循环
    this.stop();
    
    // 清理各个组件
    if (this.cameraController) {
      this.cameraController.disableControls();
    }
    
    if (this.particleEngine) {
      this.particleEngine.dispose();
    }
    
    if (this.launchController) {
      this.launchController.clear();
    }
    
    if (this.renderer) {
      this.renderer.dispose();
    }
    
    console.log('✓ Application disposed');
  }

  /**
   * 获取应用状态
   * @returns {Object}
   */
  getStatus() {
    return {
      isRunning: this.isRunning,
      fps: this.performanceMonitor ? this.performanceMonitor.getFPS() : 0,
      particleCount: this.particleEngine ? this.particleEngine.getActiveParticleCount() : 0,
      fireworkCount: this.launchController ? this.launchController.getActiveCount() : 0,
      qualityLevel: this.performanceMonitor ? this.performanceMonitor.getQualityLevel() : 'unknown'
    };
  }
}

export default FireworksApp;
